import { QuantityField } from "@/components/ClustersPage/QuantityField/QuantityField";
import { OptionType } from "@/components/forms/inputs/SelectInput";
import { RecipeRadio24 } from "./RecipeOptionBadges";
import { GpuUnavailabilityInfoModal } from "./GpuUnavailabilityInfoModal";
import { CheckRadioIcon } from "@/components/Icons/CheckRadioIcon";
import { PowerIcon } from "@/components/Icons/PowerIcon";
import { WarnTriangleIcon } from "@/components/Icons/WarnTriangleIcon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import {
  DEFAULT_GPU_UNAVAILABILITY_POLICY,
  GpuUnavailabilityPolicy,
  SimpleClusterForm
} from "@/types";
import { cn } from "@/utils/styles";
import { useFormikContext } from "formik";
import { useState } from "react";
import { SavedInConfigMarker } from "@/components/ClustersPage/SavedDeploymentConfigs/SavedInConfigMarker";

type GpusPerContainerSectionProps = {
  /** Maximum GPUs per container, derived from the selected hardware's num_cards. */
  maxGpus: number;
  /** Available replica counts; when provided with showReplicas, renders Replicas under GPUs. */
  availableQuantity?: OptionType[];
  showReplicas?: boolean;
  className?: string;
};

type PolicyOption = {
  value: GpuUnavailabilityPolicy;
  title: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  iconWrapClassName: string;
  selectedClassName: string;
};

const POLICY_OPTIONS: PolicyOption[] = [
  {
    value: "partial_refund",
    title: "Keep running, refund the rest",
    description:
      "Launch every container we can. Containers that can't start are refunded, the rest keep their original runtime.",
    icon: CheckRadioIcon,
    iconWrapClassName: "bg-[rgba(50,145,255,0.12)] text-[#3291ff]",
    selectedClassName:
      "border-[#3291ff] bg-gradient-to-br from-[rgba(50,145,255,0.10)] to-transparent"
  },
  {
    value: "extend_booked",
    title: "Keep running, extend the rest",
    description:
      "Launch every container we can and reuse the time you already paid: the running ones stay up longer instead of being refunded.",
    icon: CheckRadioIcon,
    iconWrapClassName: "bg-[rgba(64,186,111,0.14)] text-[#40ba6f]",
    selectedClassName:
      "border-[#40ba6f] bg-gradient-to-br from-[rgba(64,186,111,0.10)] to-transparent"
  },
  {
    value: "full_refund",
    title: "Fail cluster completely",
    description:
      "If any container can't start, cancel the whole deployment and refund you in full.",
    icon: PowerIcon,
    iconWrapClassName: "bg-[rgba(230,119,10,0.12)] text-[#e6770a]",
    selectedClassName:
      "border-[#e6770a] bg-gradient-to-br from-[rgba(230,119,10,0.10)] to-transparent"
  }
];

export const GpusPerContainerSection: React.FC<GpusPerContainerSectionProps> = ({
  maxGpus,
  availableQuantity = [],
  showReplicas = false,
  className
}) => {
  const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();
  const selectedPolicy = values.gpuUnavailabilityPolicy ?? DEFAULT_GPU_UNAVAILABILITY_POLICY;
  const [infoModalTab, setInfoModalTab] = useState<GpuUnavailabilityPolicy | null>(null);

  const openInfoModal = (tab: GpuUnavailabilityPolicy) => setInfoModalTab(tab);

  const showReplicaStepper = showReplicas && availableQuantity.length > 0;
  const quantityBounds = availableQuantity
    .map(({ value }) => parseInt(value, 10))
    .filter((n) => !Number.isNaN(n));
  const minQuantity = quantityBounds.length > 0 ? Math.min(...quantityBounds) : 1;
  const maxQuantity = quantityBounds.length > 0 ? Math.max(...quantityBounds) : minQuantity;

  return (
    <div className={cn("flex flex-col gap-5 overflow-hidden", className)}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
            Number of GPUs per Container
            <SavedInConfigMarker />
          </span>
          <span className="text-[14px] leading-[20px] text-muted-foreground">
            Choose how many GPUs each container replica runs on
          </span>
        </div>
        <QuantityField
          name="gpusPerContainer"
          min={1}
          max={maxGpus}
          inputClassName="w-[56px] min-w-[56px] shrink-0 flex-none px-2"
          className="shrink-0 items-center"
        />
      </div>

      {showReplicaStepper && (
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
              Replicas
              <SavedInConfigMarker />
            </span>
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              How many replicas of the container to deploy
            </span>
          </div>
          <QuantityField
            name="replicaCount"
            min={minQuantity}
            max={maxQuantity}
            placeholder={String(values.replicaCount ?? minQuantity)}
            inputClassName="w-[56px] min-w-[56px] shrink-0 flex-none px-2"
            className="shrink-0 items-center"
          />
        </div>
      )}

      <div className="flex flex-col gap-3 rounded-[12px] border border-border bg-gradient-to-b from-muted/40 to-transparent p-4">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[rgba(230,119,10,0.12)]">
            <WarnTriangleIcon className="h-4 w-4" />
          </span>
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5">
              <span className="flex items-center gap-2 text-[16px] font-medium leading-[24px] text-foreground">
                When we can't get all your GPUs
                <SavedInConfigMarker />
              </span>
              <button
                type="button"
                aria-label="Learn how this works"
                title="How does this work?"
                onClick={() => openInfoModal(selectedPolicy)}
                className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground/60 transition-colors hover:text-foreground"
              >
                <InfoCircleIcon className="h-3.5 w-3.5" />
              </button>
            </span>
            <span className="text-[14px] leading-[20px] text-muted-foreground">
              GPU demand is very high, so the exact amount you request may not always be available,
              even though we refresh availability very regularly. We'll keep partial deployments
              running by default, but you can change that below.{" "}
              <button
                type="button"
                onClick={() => openInfoModal(selectedPolicy)}
                className="font-medium text-[#3291ff] underline-offset-2 hover:underline"
              >
                Learn more
              </button>
            </span>
          </div>
        </div>

        <div
          role="radiogroup"
          aria-label="Behaviour when GPUs are unavailable"
          className="flex flex-col gap-2.5"
        >
          {POLICY_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedPolicy === option.value;
            const isRecommended = option.value === DEFAULT_GPU_UNAVAILABILITY_POLICY;
            return (
              <div
                key={option.value}
                role="radio"
                tabIndex={0}
                aria-checked={isSelected}
                onClick={() => setFieldValue("gpuUnavailabilityPolicy", option.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setFieldValue("gpuUnavailabilityPolicy", option.value);
                  }
                }}
                className={cn(
                  "flex cursor-pointer items-center gap-3 rounded-[10px] border bg-background p-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isSelected
                    ? option.selectedClassName
                    : "border-border hover:border-foreground/30 hover:bg-muted/40"
                )}
              >
                <span
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px]",
                    option.iconWrapClassName
                  )}
                >
                  <Icon className="h-[18px] w-[18px]" />
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    <span className="text-[14px] font-medium leading-5 text-foreground">
                      {option.title}
                    </span>
                    {isRecommended && (
                      <span className="rounded-full bg-[rgba(50,145,255,0.12)] px-2 py-0.5 text-[11px] font-medium leading-4 text-[#3291ff]">
                        Recommended
                      </span>
                    )}
                  </span>
                  <span className="text-[13px] leading-5 text-muted-foreground">
                    {option.description}
                  </span>
                </span>

                <RecipeRadio24 isSelected={isSelected} />
              </div>
            );
          })}
        </div>
      </div>

      <GpuUnavailabilityInfoModal
        isOpen={infoModalTab !== null}
        initialTab={infoModalTab ?? DEFAULT_GPU_UNAVAILABILITY_POLICY}
        onClose={() => setInfoModalTab(null)}
      />
    </div>
  );
};
